/**
 * Promise to Pay list filter state: defaults, validation and the mapping to query params.
 * Shared by the list page and by the promise details sidebar, which restores the same
 * persisted filter state from sessionStorage.
 */

import type { GetPromisesToPayParams } from '@/hooks/usePromisesToPayData';
import { isRangeInvalid, RANGE_MIN_GREATER_THAN_MAX_MESSAGE } from '@/lib/range-filter-validation';
import { toLocalDateString } from '@/lib/utils';

export const PROMISES_LIST_FILTERS_KEY = 'promises-list-filters';

export interface PromisesAdvancedFilters {
  statuses: string[];
  assignedUser: string;
  createdBy: string;
  currencies: string[];
  confirmationMethods: string[];
  amountMin: string;
  amountMax: string;
  minTransactions: string;
  dueToday: boolean;
  promiseDateFrom: string;
  promiseDateTo: string;
  daysOverdueMin: string;
  createdAtFrom: string;
  createdAtTo: string;
}

export const DEFAULT_PROMISES_FILTERS: PromisesAdvancedFilters = {
  statuses: ['Active', 'Broken', 'Partially Paid'],
  assignedUser: 'all',
  createdBy: 'all',
  currencies: [],
  confirmationMethods: [],
  amountMin: '',
  amountMax: '',
  minTransactions: '',
  dueToday: false,
  promiseDateFrom: '',
  promiseDateTo: '',
  daysOverdueMin: '',
  createdAtFrom: '',
  createdAtTo: '',
};

export interface PersistedPromisesFilters {
  advancedFilters: PromisesAdvancedFilters;
  searchQuery: string;
}

export type NumericFilterField = 'amountMin' | 'amountMax' | 'minTransactions' | 'daysOverdueMin';

export type PromisesFilterErrors = Partial<Record<NumericFilterField, string>>;

export function todayAsIsoDate(): string {
  return toLocalDateString(new Date());
}

export function parseNumericFilter(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const NUMERIC_FILTER_LABELS: Array<{ field: NumericFilterField; label: string; wholeNumber: boolean }> = [
  { field: 'amountMin', label: 'Min amount', wholeNumber: false },
  { field: 'amountMax', label: 'Max amount', wholeNumber: false },
  { field: 'minTransactions', label: 'Min. transactions', wholeNumber: true },
  { field: 'daysOverdueMin', label: 'Days overdue', wholeNumber: true },
];

export function validateNumericFilters(filters: PromisesAdvancedFilters): PromisesFilterErrors {
  const errors: PromisesFilterErrors = {};

  for (const { field, label, wholeNumber } of NUMERIC_FILTER_LABELS) {
    const rawValue = filters[field];
    if (rawValue.trim() === '') continue;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      errors[field] = `${label} must be a number`;
    } else if (parsed < 0) {
      errors[field] = `${label} cannot be negative`;
    } else if (wholeNumber && !Number.isInteger(parsed)) {
      errors[field] = `${label} must be a whole number`;
    }
  }

  if (
    !errors.amountMin &&
    !errors.amountMax &&
    isRangeInvalid(parseNumericFilter(filters.amountMin), parseNumericFilter(filters.amountMax))
  ) {
    errors.amountMax = RANGE_MIN_GREATER_THAN_MAX_MESSAGE;
  }

  return errors;
}

/** Restores a persisted/partial filter object, keeping Due Today consistent with the promise date range. */
export function normalizePromisesFilters(filters: Partial<PromisesAdvancedFilters>): PromisesAdvancedFilters {
  const merged = { ...DEFAULT_PROMISES_FILTERS, ...filters };
  if (merged.dueToday && !merged.promiseDateFrom && !merged.promiseDateTo) {
    const today = todayAsIsoDate();
    return { ...merged, promiseDateFrom: today, promiseDateTo: today };
  }
  return merged;
}

/** Number of filter groups holding a non-default value (drives the Filters badge). */
export function countActivePromisesFilters(filters: PromisesAdvancedFilters): number {
  let count = 0;
  if (JSON.stringify(filters.statuses) !== JSON.stringify(DEFAULT_PROMISES_FILTERS.statuses)) count += 1;
  if (filters.assignedUser !== 'all') count += 1;
  if (filters.createdBy !== 'all') count += 1;
  if (filters.currencies.length > 0) count += 1;
  if (filters.confirmationMethods.length > 0) count += 1;
  if (filters.amountMin || filters.amountMax) count += 1;
  if ((parseNumericFilter(filters.minTransactions) ?? 0) > 0) count += 1;
  if (filters.promiseDateFrom || filters.promiseDateTo) count += 1;
  if ((parseNumericFilter(filters.daysOverdueMin) ?? 0) > 0) count += 1;
  if (filters.createdAtFrom || filters.createdAtTo) count += 1;
  return count;
}

/** Maps the drawer filter state onto the server-side query params. */
export function buildPromisesFilterParams(filters: PromisesAdvancedFilters): GetPromisesToPayParams {
  const params: GetPromisesToPayParams = {};

  // Status filter (OR logic: Active OR Broken etc.)
  if (filters.statuses.length > 0) {
    params.statuses = filters.statuses;
  }

  // Promise date range (the Due Today quick filter pre-fills this range)
  if (filters.promiseDateFrom) {
    params.dateFrom = filters.promiseDateFrom;
  }
  if (filters.promiseDateTo) {
    params.dateTo = filters.promiseDateTo;
  }

  // User filters
  const assignedUserId = Number(filters.assignedUser);
  if (filters.assignedUser !== 'all' && Number.isFinite(assignedUserId)) {
    params.assignedUserId = assignedUserId;
  }
  const createdByUserId = Number(filters.createdBy);
  if (filters.createdBy !== 'all' && Number.isFinite(createdByUserId)) {
    params.createdByUserId = createdByUserId;
  }

  if (filters.currencies.length > 0) {
    params.currencyCodes = filters.currencies;
  }

  if (filters.confirmationMethods.length > 0) {
    params.confirmationMethods = filters.confirmationMethods;
  }

  // Promised amount range
  const amountMin = parseNumericFilter(filters.amountMin);
  const amountMax = parseNumericFilter(filters.amountMax);
  if (amountMin !== undefined) {
    params.amountMin = amountMin;
  }
  if (amountMax !== undefined) {
    params.amountMax = amountMax;
  }

  // Minimum transactions / days overdue (0 or empty means no filter)
  const minTransactions = parseNumericFilter(filters.minTransactions);
  if (minTransactions !== undefined && minTransactions > 0) {
    params.minTransactions = minTransactions;
  }
  const daysOverdueMin = parseNumericFilter(filters.daysOverdueMin);
  if (daysOverdueMin !== undefined && daysOverdueMin > 0) {
    params.daysOverdueMin = daysOverdueMin;
  }

  // Created At date range
  if (filters.createdAtFrom) {
    params.createdAtFrom = filters.createdAtFrom;
  }
  if (filters.createdAtTo) {
    params.createdAtTo = filters.createdAtTo;
  }

  return params;
}
